import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgbAccordionItem } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AppFacadeService } from 'src/app/app-facade.service';
import {
    MACHINE_LEARNING_ROUTE_PART,
    PERMISSION_APPLICATIONS,
    PERMISSION_COMMUNITYCONTENT,
    PERMISSION_COMMUNITYCONTENT_PUBLICMODULES,
    PERMISSION_COMMUNITYCONTENT_PUBLICWORKFLOWS,
    PERMISSION_INFORMATION,
    PERMISSION_INFORMATION_APEER,
    PERMISSION_INFORMATION_BLOG,
    PERMISSION_INFORMATION_DOCS,
    PERMISSION_MACHINELEARNING,
    PERMISSION_MACHINELEARNING_ANNOTATE,
    PERMISSION_MACHINELEARNING_OVERVIEW,
    PERMISSION_MACHINELEARNING_SEGMENT,
    PERMISSION_MACHINELEARNING_TRAIN,
    PERMISSION_MYLAB,
    PERMISSION_MYLAB_FILES,
    PERMISSION_MYLAB_MODULES,
    PERMISSION_MYLAB_PROJECTS,
    PERMISSION_MYLAB_RESULTS,
    PERMISSION_MYLAB_WORKFLOWS,
    PUBLIC_MODULES_ROUTE,
    PUBLIC_WORKFLOWS_ROUTE,
    USER_ROUTE_PART,
} from '../../../shared/constants/constants';
import { Menu } from '../../models/menu.model';
import { NavigationSection } from '../../models/navigation.model';
import { Submenu } from '../../models/submenu.model';
import { UserInfo } from '../../models/user-info.model';
import { ExperimentalFeaturesService } from '../../services/experimental-features.service';
import { UserWorkspaceService } from '../../services/user-workspace.service';
import { UserService } from '../../services/user.service';

@UntilDestroy()
@Component({
    selector: 'ca-sidebar-nav',
    templateUrl: './sidebar-nav.component.html',
    styleUrls: ['./sidebar-nav.component.scss'],
})
export class SidebarNavComponent implements OnInit {
    @Input() set isOpenToggled(isOpenToggled: boolean | undefined) {
        // If the Opening/Closing is triggered from outside (= from the ng-sidebar component itself), the state needs to be tracked as well.
        if (isOpenToggled != null) {
            this.isNavbarOpen = isOpenToggled === true;
        }
    }

    @Input() isSmallDevice: boolean | undefined;

    @Output() openToggledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    isNavbarOpen = true;
    openPanelState = {
        'ai-toolkit-panel': true,
        'more-information-panel': true,
        'my-lab-panel': true,
        'public-panel': true,
    };
    expandedPanels: NavigationSection[] = [];

    myWorkflowsCount$: Observable<number> = this.userWorkspaceService.myWorkflowsTotalCount$;
    myModulesTotalCount$: Observable<number> = this.appFacadeService.myModulesTotalCount$;
    myResultsCount$: Observable<number> = this.userWorkspaceService.myResultsTotalCount$;

    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    menu: Menu[] = [];
    homeSubmenu: Submenu[] = [];
    mainMenu: Menu[] = [];

    get isLoggedIn(): boolean {
        return this.userService.isLoggedIn;
    }

    get userInfo(): UserInfo {
        return this.userService.myInfo!;
    }

    constructor(
        private router: Router,
        private userService: UserService,
        private appFacadeService: AppFacadeService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private userWorkspaceService: UserWorkspaceService,

        private translateService: TranslateService,
    ) {}
    logout(): void {
        this.userService.logout();
    }

    login(): void {
        this.router.navigate(['implicit-login']);
    }

    toggleSidebar(): void {
        this.isNavbarOpen = !this.isNavbarOpen;
        this.openToggledChange.emit(this.isNavbarOpen);
    }

    refreshExpandedPanels(event: NgbAccordionItem): void {
        this.openPanelState[event.id] = !this.openPanelState[event.id];
        this.expandedPanels = this.getExpandedPanels(this.openPanelState);
    }

    hasPermissions(requiredPermissions: string[]): boolean {
        return this.userService.hasPermissions(requiredPermissions);
    }

    ngOnInit(): void {
        this.userService.permissions$.pipe(untilDestroyed(this)).subscribe(() => {
            this.menu = this.getMenu();
            this.homeSubmenu = this.getHomeSubmenu();
            this.mainMenu = this.getMainMenu();
        });
        this.expandedPanels = this.getExpandedPanels(this.openPanelState);
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.isSmallDevice && this.isNavbarOpen) {
                    this.toggleSidebar();
                }
            }
            if (event instanceof NavigationEnd) {
                let sectionToOpen: NavigationSection | undefined;
                if (event.url.includes(USER_ROUTE_PART)) {
                    sectionToOpen = 'my-lab-panel';
                } else if (event.url.includes(MACHINE_LEARNING_ROUTE_PART)) {
                    sectionToOpen = 'ai-toolkit-panel';
                } else if (event.url.includes(PUBLIC_MODULES_ROUTE) || event.url.includes(PUBLIC_WORKFLOWS_ROUTE)) {
                    sectionToOpen = 'public-panel';
                }
                this.addRoutedSectionToOpenedPanels(sectionToOpen);
            }
        });
    }
    private getMenu(): Menu[] {
        return [
            {
                id: 'home',
                order: 0,
                submenu: [
                    {
                        link: '/applications',
                        requiredPermissions: [PERMISSION_APPLICATIONS],
                        tooltip$: this.translateService.get('SIDE_BAR.APPLICATIONS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.APPLICATIONS.LINK'),
                        iconName: 'private-workflow',
                        dataUit: 'applications-selector',
                    },
                ],
            },
            {
                id: 'ai-toolkit-panel',
                title$: this.translateService.get('SIDE_BAR.SECTION.AI_TOOLKIT'),
                requiredPermissions: [PERMISSION_MACHINELEARNING_OVERVIEW],
                order: this.hasPermissions([PERMISSION_MYLAB, PERMISSION_MYLAB_PROJECTS]) ? 2 : 1,
                dataUit: 'ai-toolkit-panel-selector',
                submenu: [
                    {
                        link: 'ai-toolkit/overview',
                        requiredPermissions: [PERMISSION_MACHINELEARNING_OVERVIEW],
                        tooltip$: this.translateService.get('SIDE_BAR.ML_INTRODUCTION.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.ML_INTRODUCTION.LINK'),
                        iconName: 'ml',
                        dataUit: 'ai-toolkit-introduction-selector',
                    },
                    {
                        link: '/ai-toolkit/my-datasets',
                        requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_ANNOTATE],
                        tooltip$: this.translateService.get('SIDE_BAR.DATASETS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.DATASETS.LINK'),
                        iconName: 'pen-line',
                        dataUit: 'ai-toolkit-datasets-selector',
                    },
                    {
                        link: '/ai-toolkit/my-models',
                        requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_TRAIN],
                        tooltip$: this.translateService.get('SIDE_BAR.MODELS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.MODELS.LINK'),
                        iconName: 'training',
                        dataUit: 'ai-toolkit-models-selector',
                    },
                    {
                        link: '/ai-toolkit/my-segmentations',
                        requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_SEGMENT],
                        tooltip$: this.translateService.get('SIDE_BAR.SEGMENTATION.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.SEGMENTATION.LINK'),
                        iconName: 'segment',
                        dataUit: 'ai-toolkit-segment-selector',
                    },
                ],
            },
            {
                id: 'public-panel',
                title$: this.translateService.get('SIDE_BAR.SECTION.COMMUNITY_CONTENT'),
                requiredPermissions: [PERMISSION_COMMUNITYCONTENT],
                order: 2,
                dataUit: 'public-panel-selector',
                submenu: [
                    {
                        link: '/workflows',
                        requiredPermissions: [PERMISSION_COMMUNITYCONTENT, PERMISSION_COMMUNITYCONTENT_PUBLICWORKFLOWS],
                        tooltip$: this.translateService.get('SIDE_BAR.PUBLIC_WORKFLOWS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.PUBLIC_WORKFLOWS.LINK'),
                        iconName: 'public-workflow',
                        dataUit: 'public-workflows-selector',
                    },
                    {
                        link: '/modules',
                        requiredPermissions: [PERMISSION_COMMUNITYCONTENT, PERMISSION_COMMUNITYCONTENT_PUBLICMODULES],
                        tooltip$: this.translateService.get('SIDE_BAR.PUBLIC_MODULES.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.PUBLIC_MODULES.LINK'),
                        iconName: 'public-module',
                        dataUit: 'public-modules-selector',
                    },
                ],
            },
            {
                id: 'my-lab-panel',
                title$: this.translateService.get('SIDE_BAR.SECTION.MY_LAB'),
                requiredPermissions: [PERMISSION_MYLAB],
                order: this.hasPermissions([PERMISSION_MYLAB, PERMISSION_MYLAB_PROJECTS]) ? 1 : 3,
                dataUit: 'my-lab-panel-selector',
                submenu: [
                    {
                        link: '/user/workspace/workflows',
                        requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_WORKFLOWS],
                        tooltip$: this.translateService.get('SIDE_BAR.WORKFLOWS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.WORKFLOWS.LINK'),
                        count$: this.myWorkflowsCount$,
                        iconName: 'private-workflow',
                        dataUit: 'my-workflows-selector',
                    },
                    {
                        link: '/user/workspace/modules',
                        requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_MODULES],
                        tooltip$: this.translateService.get('SIDE_BAR.MODULES.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.MODULES.LINK'),
                        count$: this.myModulesTotalCount$,
                        iconName: 'private-module',
                        dataUit: 'my-modules-selector',
                    },
                    {
                        link: this.isMEDProjectEnabled ? '/user/workspace/data' : '/user/workspace/files',
                        requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_FILES],
                        tooltip$: this.translateService.get(this.isMEDProjectEnabled ? 'SIDE_BAR.DATA.TOOLTIP' : 'SIDE_BAR.FILES.TOOLTIP'),
                        linkName$: this.translateService.get(this.isMEDProjectEnabled ? 'SIDE_BAR.DATA.LINK' : 'SIDE_BAR.FILES.LINK'),
                        iconName: 'folder',
                        dataUit: 'my-files-selector',
                    },
                    {
                        link: '/user/workspace/results',
                        requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_RESULTS],
                        tooltip$: this.translateService.get('SIDE_BAR.RESULTS.TOOLTIP'),
                        linkName$: this.translateService.get('SIDE_BAR.RESULTS.LINK'),
                        count$: this.myResultsCount$,
                        iconName: 'result',
                        dataUit: 'my-results-selector',
                    },
                ],
            },
            {
                id: 'more-information-panel',
                title$: this.translateService.get('SIDE_BAR.SECTION.MORE_INFORMATION'),
                requiredPermissions: [PERMISSION_INFORMATION],
                order: 4,
                dataUit: 'more-information-panel-selector',
                submenu: this.getMoreInformationSubmenu(),
            },
        ].sort((a, b): 0 | 1 | -1 => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
    }

    private getHomeSubmenu(): Submenu[] {
        const homeMenu = this.menu.find(m => m.id === 'home');
        if (homeMenu) {
            return homeMenu.submenu;
        }
        return [];
    }

    private getMoreInformationSubmenu(): Submenu[] {
        const commonItems = [
            {
                externalLink: 'https://apeer.com/home',
                requiredPermissions: [PERMISSION_INFORMATION, PERMISSION_INFORMATION_APEER],
                tooltip$: this.translateService.get('SIDE_BAR.ARIVIS_CLOUD.TOOLTIP'),
                linkName$: this.translateService.get('SIDE_BAR.ARIVIS_CLOUD.LINK'),
                iconName: 'apeer',
                iconName2: 'external-link',
                dataUit: 'apeer-selector',
            },
        ];

        const MEDProjectItems = [
            {
                externalLink: 'https://www.zeiss.com/rdp',
                requiredPermissions: [PERMISSION_INFORMATION],
                tooltip$: this.translateService.get('SIDE_BAR.MED.LANDING_PAGE.TOOLTIP'),
                linkName$: this.translateService.get('SIDE_BAR.MED.LANDING_PAGE.LINK'),
                iconName: 'apeer',
                iconName2: 'external-link',
                dataUit: 'ZEISS-RDP-selector',
            },
            {
                externalLink: 'https://zeiss.ly/rdp-foss-disclosure',
                requiredPermissions: [PERMISSION_INFORMATION],
                tooltip$: this.translateService.get('SIDE_BAR.MED.FOSS_STATEMENT.TOOLTIP'),
                linkName$: this.translateService.get('SIDE_BAR.MED.FOSS_STATEMENT.LINK'),
                iconName: 'docs',
                iconName2: 'external-link',
                dataUit: 'foss-disclosure-selector',
            },
        ];

        const arivisCloudProjectItems = [
            {
                externalLink: 'https://docs.apeer.com/',
                requiredPermissions: [PERMISSION_INFORMATION, PERMISSION_INFORMATION_DOCS],
                tooltip$: this.translateService.get('SIDE_BAR.DOCS.TOOLTIP'),
                linkName$: this.translateService.get('SIDE_BAR.DOCS.LINK'),
                iconName: 'docs',
                iconName2: 'external-link',
                dataUit: 'more-information-docs-selector',
            },
            {
                externalLink: 'https://www.apeer.com/blog',
                requiredPermissions: [PERMISSION_INFORMATION, PERMISSION_INFORMATION_BLOG],
                tooltip$: this.translateService.get('SIDE_BAR.BLOG.TOOLTIP'),
                linkName$: this.translateService.get('SIDE_BAR.BLOG.LINK'),
                iconName: 'blog',
                iconName2: 'external-link',
                dataUit: 'more-information-blog-selector',
            },
        ];

        return this.isMEDProjectEnabled ? [...MEDProjectItems, ...commonItems] : [...commonItems, ...arivisCloudProjectItems];
    }

    private getMainMenu(): Menu[] {
        return this.menu.filter(m => m.id !== 'home');
    }

    private addRoutedSectionToOpenedPanels(sectionToOpen: NavigationSection | undefined): void {
        if (sectionToOpen) {
            this.openPanelState[sectionToOpen] = true;
            this.expandedPanels = this.getExpandedPanels(this.openPanelState);
        }
    }

    private getExpandedPanels(openPanelState): NavigationSection[] {
        const expandedPanels: NavigationSection[] = [];
        for (const key in openPanelState) {
            if (openPanelState.hasOwnProperty(key)) {
                if (openPanelState[key]) {
                    expandedPanels.push(key as NavigationSection);
                }
            }
        }
        return expandedPanels;
    }
}
